//auth 2.0
/**
 * Build styles
 */
import "./index.css";
import store from "redux/store";
import * as Sentry from "@sentry/browser";
import { alertImageTooLarge } from "components/alerts";
import { createDocument, registerClip } from "redux/actions/vdocsActions";
import { uploadFileImage } from "utils/aws/s3Interface";
import { toBase64 } from "utils/utils";
import { sendAmplitudeData } from "utils/amplitude";
import env from "config/env";
import i18n from "config/i18n/i18n";
const t = i18n.t.bind(i18n);

const SLID_WEB_APP_URL = env.end_point_url.slid_web_app;

export default class ImageUploader {
  /**
   * Notify core that read-only mode is supported
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Allow to press Enter inside the CodeTool textarea
   *
   * @returns {boolean}
   * @public
   */
  static get enableLineBreaks() {
    return true;
  }

  /**
   * @typedef {object} CodeData — plugin saved data
   * @property {string} code - previously saved plugin code
   */

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {object} options - tool constricting options
   * @param {CodeData} options.data — previously saved plugin code
   * @param {object} options.config - user config for Tool
   * @param {object} options.api - Editor.js API
   * @param {boolean} options.readOnly - read only mode flag
   */

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.config = config;
    this.readOnly = readOnly;

    this.slidGlobal = store.getState().slidGlobal;
    this.lang = this.slidGlobal.lang;
    this.vdocs = store.getState().vdocs;
    this.blockIndex = this.api.blocks.getCurrentBlockIndex();
    this.isDemoMode = document.location.href === `${SLID_WEB_APP_URL}/demo`;

    this.CSS = {
      baseClass: this.api.styles.block,
      input: "ce-image-loader__input",
      fileUploadButton: "cdx-button",
      wrapper: "ce-image-loader",
      disabled: "ce-image-loader__disabled",
    };

    this.nodes = {
      holder: null,
    };

    this.data = {
      code: data.code || "",
    };

    this.nodes.holder = this.drawView();
  }

  /**
   * Create Tool's view
   *
   * @returns {HTMLElement}
   * @private
   */
  drawView() {
    const wrapper = document.createElement("div");
    const selectFileButton = document.createElement("label");
    const input = document.createElement("input");

    if (this.readOnly) {
      input.disabled = true;
    }

    wrapper.classList.add(this.CSS.baseClass, this.CSS.wrapper);
    input.classList.add(this.CSS.input);
    selectFileButton.classList.add(this.CSS.fileUploadButton, "ce-image-loader__button");

    input.id = "image-uploader";
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.onchange = (event) => {
      this.uploadImageFile(event);
    };

    selectFileButton.setAttribute("for", "image-uploader");
    selectFileButton.innerHTML = t("SelectAnImage", { ns: "common" });
    selectFileButton.disabled = true;

    wrapper.appendChild(selectFileButton);
    wrapper.appendChild(input);

    return wrapper;
  }

  /**
   * Return Tool's view
   *
   * @returns {HTMLDivElement} this.nodes.holder - Code's wrapper
   * @public
   */
  render() {
    return this.nodes.holder;
  }

  /**
   * Fires after clicks on the Toolbox Image Icon
   * Initiates click on the Select File button
   *
   * @public
   */
  appendCallback() {
    const ImageUploadInput = this.nodes.holder.childNodes[1];
    ImageUploadInput.click();
  }

  uploadImageFile(event) {
    // 10.0 Megabytes (MB)	=	10,485,760 Bytes (B)
    const uploadedFile = event.target.files[0];
    const MAX_FILE_SIZE = 10485760;

    // This method upload pdf to s3 and interval request for extracting pdf.
    const onImageUpload = async ({ file }) => {
      const rawSrc = await toBase64(file);

      if (this.isDemoMode) {
        this.api.blocks.delete(this.blockIndex);
        this.api.blocks.insert(
          "image",
          {
            rawSrc: rawSrc,
            type: "filePaste",
          },
          undefined,
          this.blockIndex,
          false
        );
        this.api.caret.setToNextBlock();
        this.api.blocks.getBlockByIndex(this.blockIndex).holder.scrollIntoView();
        return;
      }

      const { currentDocument } = this.vdocs;

      if (!currentDocument) await this.api.saver.save();

      // To show raw image first
      const getPasteImageBlockData = async (currentDocumentKey, file) => {
        const uploadedImageSrc = await uploadFileImage({
          path: `image_upload/${currentDocumentKey}`,
          file: file,
        });

        if (!uploadedImageSrc) {
          Sentry.withScope((scope) => {
            scope.setLevel("error");
            scope.setExtra("message", "s3 upload failed");
            Sentry.captureMessage("SLID_WEB_ADD_IMAGE_BY_PASTE_ERROR");
          });
          return;
        }

        const clipRegisterResponse = await store.dispatch(
          registerClip({
            imgSrc: uploadedImageSrc,
            documentKey: currentDocumentKey,
          })
        );

        if (clipRegisterResponse.error_message) return;

        return {
          clipKey: clipRegisterResponse["clip_key"],
          src: uploadedImageSrc,
          documentKey: currentDocumentKey,
          type: "filePaste",
        };
      };

      let currentDocumentKey;

      // check current document
      if (currentDocument) {
        currentDocumentKey = currentDocument["document_key"];
      } else {
        const createdDocument = await store.dispatch(
          createDocument({
            origin: this.mode,
          })
        );
        if (createdDocument.error_message) return;
        currentDocumentKey = createdDocument["document_key"];
      }
      this.api.blocks.delete(this.blockIndex);
      this.api.blocks.insert(
        "image",
        {
          documentKey: currentDocumentKey,
          rawSrc: rawSrc,
          type: "filePaste",
          setClipData: async () => {
            return await getPasteImageBlockData(currentDocumentKey, file);
          },
        },
        undefined,
        this.blockIndex,
        false
      );
      this.api.caret.setToNextBlock();
      this.api.blocks.getBlockByIndex(this.blockIndex).holder.scrollIntoView();

      sendAmplitudeData(`Insert image`, {
        origin: this.mode,
        type: "image file uploaded",
      });
    };

    if (uploadedFile?.size > MAX_FILE_SIZE) {
      alertImageTooLarge();
    } else {
      onImageUpload({ file: uploadedFile });
    }
  }

  /**
   * Extract Tool's data from the view
   *
   * @param {HTMLDivElement} codeWrapper - CodeTool's wrapper, containing textarea with code
   * @returns {CodeData} - saved plugin code
   * @public
   */
  save() {
    return this.data;
  }

  /**
   * Returns Tool`s data from private property
   *
   * @returns {CodeData}
   */
  get data() {
    return this._data;
  }

  /**
   * Set Tool`s data to private property and update view
   *
   * @param {CodeData} data - saved tool data
   */
  set data(data) {
    this._data = data || {};
  }

  /**
   * Get Tool toolbox settings
   * icon - Tool icon's SVG
   * title - title to show in toolbox
   *
   * @returns {{icon: string, title: string}}
   */
  static get toolbox() {
    return {
      icon: `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.09091 1C1.0407 1 1 1.0407 1 1.09091V8.64545L1.92456 7.56372C1.93694 7.54924 1.95013 7.53547 1.96408 7.5225C2.07057 7.42343 2.19515 7.3469 2.3295 7.29536C2.46381 7.24384 2.60662 7.21776 2.74996 7.21776C2.89329 7.21776 3.0361 7.24384 3.17042 7.29536C3.30409 7.34664 3.42809 7.42266 3.53422 7.52101L5.14521 8.9969C5.15503 9.00603 5.16909 9.01544 5.18764 9.02255C5.20623 9.02968 5.22753 9.03382 5.24996 9.03382C5.27238 9.03382 5.29368 9.02968 5.31227 9.02255C5.33082 9.01544 5.34489 9.00604 5.35471 8.99691L5.3575 8.99431L8.83908 5.80473L8.8407 5.80323C8.94684 5.70489 9.07083 5.62887 9.2045 5.5776C9.33881 5.52607 9.48162 5.5 9.62496 5.5C9.76829 5.5 9.9111 5.52607 10.0454 5.5776C10.1745 5.62711 10.2945 5.69969 10.3982 5.79317L13 7.93938V1.09091C13 1.0407 12.9593 1 12.9091 1H1.09091ZM2.65854 8.24408L1 10.1846V12.9091C1 12.9593 1.0407 13 1.09091 13H12.9091C12.9593 13 13 12.9593 13 12.9091V9.23571L9.7521 6.55653C9.74444 6.55021 9.73698 6.54366 9.72971 6.5369C9.71989 6.52777 9.70582 6.51838 9.68727 6.51126C9.66868 6.50413 9.64738 6.5 9.62496 6.5C9.60253 6.5 9.58123 6.50413 9.56264 6.51126C9.54409 6.51838 9.53002 6.52777 9.5202 6.5369L9.51741 6.5395L6.03583 9.72908L6.03421 9.73058C5.92808 9.82892 5.80408 9.90494 5.67042 9.95622C5.5361 10.0077 5.39329 10.0338 5.24996 10.0338C5.10662 10.0338 4.96381 10.0077 4.8295 9.95622C4.69582 9.90494 4.57182 9.82891 4.46568 9.73056L2.8547 8.25468C2.84488 8.24554 2.83082 8.23614 2.81227 8.22902C2.79368 8.2219 2.77238 8.21776 2.74996 8.21776C2.72753 8.21776 2.70623 8.2219 2.68764 8.22902C2.67628 8.23338 2.66659 8.2386 2.65854 8.24408ZM14 12.9091V9.00511V8.99545V1.09091C14 0.488417 13.5116 0 12.9091 0H1.09091C0.488417 0 0 0.488417 0 1.09091V9.99977V12.9091C0 13.5116 0.488417 14 1.09091 14H12.9091C13.5116 14 14 13.5116 14 12.9091ZM5.75 4.1875C5.75 4.70527 5.33027 5.125 4.8125 5.125C4.29473 5.125 3.875 4.70527 3.875 4.1875C3.875 3.66973 4.29473 3.25 4.8125 3.25C5.33027 3.25 5.75 3.66973 5.75 4.1875Z" fill="currentColor" /></svg>`,
      title: "ImageUploader",
    };
  }
}
